import { apiCall, isBackendConfigured } from './api-call'
import { AnonInquiryResult, ResolvedAnonRenter } from '../_types/anon-session'

/**
 * Mints a cross-site anonymous session for the currently signed-in (anonymous
 * Firebase) renter. Called from inside the first-party popup, where the
 * anonymous auth session exists, so `apiCall` can attach its ID token.
 *
 * @param {string} quickPassUserId - the lite QuickPass user just provisioned in the popup
 * @returns {Promise<string | null>} the session token, or null when backend is unconfigured
 */
const createAnonSession = async (
  quickPassUserId: string,
): Promise<string | null> => {
  if (isBackendConfigured() === false) {
    return null
  }

  const response = await apiCall({
    group: 'quickpass',
    command: 'createanonsession',
    data: { quickPassUserId },
  })

  if (response.code !== 200) {
    throw new Error(response.message)
  }

  const parsed = JSON.parse(response.data) as { token: string }
  return parsed.token
}

/**
 * Resolves a cross-site session token back into the renter's contact details
 * so an embedded inquiry form can pre-fill them. Returns null when the token
 * is unknown/expired or backend is unconfigured.
 *
 * @param {string} token - the session token read from the `qp_id` cookie
 * @returns {Promise<ResolvedAnonRenter | null>}
 */
const resolveAnonSession = async (
  token: string,
): Promise<ResolvedAnonRenter | null> => {
  if (isBackendConfigured() === false) {
    return null
  }

  try {
    const response = await apiCall({
      group: 'quickpass',
      command: 'resolveanonsession',
      data: { token },
    })

    if (response.code === 404) {
      return null
    }

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as ResolvedAnonRenter
  } catch (error) {
    console.warn('[anon-session] resolve failed —', error)
    return null
  }
}

interface SubmitAnonymousInquiryInput {
  token: string
  inventoryId: string
  message: string
  customFields: Record<string, string>
}

/**
 * Submits an inquiry on behalf of an anonymous renter, identified by their
 * cross-site session token. Returns null when backend is unconfigured.
 *
 * @param {SubmitAnonymousInquiryInput} input - the session token, the listing's inventory id, and the renter's message
 * @returns {Promise<AnonInquiryResult | null>}
 */
const submitAnonymousInquiry = async (
  input: SubmitAnonymousInquiryInput,
): Promise<AnonInquiryResult | null> => {
  if (isBackendConfigured() === false) {
    return null
  }

  const response = await apiCall({
    group: 'quickpass',
    command: 'submitanonymousinquiry',
    data: {
      token: input.token,
      inventoryId: input.inventoryId,
      message: input.message,
      customFields: input.customFields,
    },
  })

  if (response.code !== 200) {
    throw new Error(response.message)
  }

  return JSON.parse(response.data) as AnonInquiryResult
}

export { createAnonSession, resolveAnonSession, submitAnonymousInquiry }
